import { Component, HostListener, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { AuthenticationService } from '@services/authentication.service'
import { SubscriptionService } from '@services/subscription.service'
import { UserService } from '@services/user.service'
import { RuntimeCapabilitiesService } from '@services/runtime-capabilities.service'
import { finalize } from 'rxjs/operators'

type IconTone = 'danger' | 'warning' | 'muted'
type PrimaryKind = 'navigate' | 'retry' | 'billingPortal' | 'reactivate' | 'none'

type StaticContent = {
  title: string
  iconClass: string
  iconTone: IconTone
}

const staticContent: Record<string, StaticContent> = {
  '503': { title: 'Account access is unavailable', iconClass: 'fa-triangle-exclamation', iconTone: 'warning' },
  '402': { title: 'Account access needs attention', iconClass: 'fa-lock', iconTone: 'warning' },
  '403': { title: 'Access Denied', iconClass: 'fa-lock', iconTone: 'muted' },
  '404': { title: 'Project Not Found', iconClass: 'fa-map-location-dot', iconTone: 'muted' },
}

const fallbackStatic: StaticContent = {
  title: 'Something went wrong',
  iconClass: 'fa-triangle-exclamation',
  iconTone: 'muted',
}


@Component({
  selector: 'shared-error-page',
  templateUrl: './error-page.component.html',
  styleUrls: ['./error-page.component.css'],
  standalone: false
})
export class ErrorPageComponent implements OnInit {
  public isOpening = false
  public accessUnavailable = false
  public accessMissing = false

  get errorCode() { return this.params.code }
  get params() { return this.route.snapshot.params }
  get user() { return this.userService.currentUser }

  get legacyRecovery() { return this.capabilities.legacyStripe && this.errorCode === '402' }
  get subscriptionNeedsReactivation() { return ['canceled', 'incomplete_expired'].includes(this.subscriptionService.details?.status) }
  get isAdmin() { return this.authService.hasUserAdminAccess }
  get portalReturnUrl() { return `${window.location.origin}/error/${this.errorCode}` }

  get iconClass() { if (this.legacyRecovery) return 'fa-credit-card'; return (staticContent[this.errorCode] ?? fallbackStatic).iconClass }
  get iconTone(): IconTone { if (this.legacyRecovery) return 'danger'; return (staticContent[this.errorCode] ?? fallbackStatic).iconTone }
  get title() { if (this.legacyRecovery) return 'Your subscription needs attention'; return (staticContent[this.errorCode] ?? fallbackStatic).title }

  get lead(): string {
    if (this.legacyRecovery && !this.accessUnavailable) return this.subscriptionNeedsReactivation
      ? "Your organization's subscription has been canceled."
      : "Your organization's last payment didn't go through."
    if (this.errorCode === '402') {
      if (this.accessMissing) return 'This account has not been set up for access yet.'
      if (this.accessUnavailable) return 'We could not load your account access.'
      const status = this.subscriptionService.details?.status
      if (status === 'suspended') return 'Access to this account is suspended or has not started yet.'
      if (status === 'ended') return 'Access to this account has ended.'
      return 'Access to this account is not currently available.'
    }
    if (this.errorCode === '503') return 'We could not verify account access for this account.'
    if (this.errorCode === '403') return "You don't have access to this part of the app."
    if (this.errorCode === '404') return "We couldn't find the page you were looking for."
    return "We couldn't complete that request."
  }

  get body(): string {
    if (this.errorCode === '503') return 'Retry in a moment. If this continues, contact your organization administrator or support@kartorium.com. You can sign out to choose another account.'
    if (this.legacyRecovery && !this.accessUnavailable) {
      if (this.subscriptionNeedsReactivation) return this.isAdmin
        ? 'Reactivate it to restore access â€” it will pick up the same plan and seats as before.'
        : 'Ask your organization admin to reactivate it, or email support@kartorium.com.'
      return 'Update your payment method to restore access â€” it takes about a minute.'
    }
    if (this.errorCode === '402') {
      return this.accessUnavailable
        ? 'Try again. If the problem continues, contact support@kartorium.com.'
        : 'Contact your organization administrator or support@kartorium.com to review access.'
    }
    if (this.errorCode === '403') {
      return 'Ask your organization administrator to grant you access, then come back.'
    }
    if (this.errorCode === '404') {
      return 'The link may have moved, or the project may have been deleted.'
    }
    return 'Try again in a moment.'
  }

  get hasPrimaryAction(): boolean { return this.primaryKind !== 'none' }
  get hasSecondaryAction(): boolean { return this.errorCode === '402' || this.errorCode === '503' }

  get primaryActionLabel(): string {
    switch (this.primaryKind) {
      case 'retry': return 'Check Access Again'
      case 'billingPortal': return 'Update Payment Method'
      case 'reactivate': return 'Reactivate Subscription'
      case 'none': return ''
      case 'navigate': return this.errorCode === '503' ? 'Retry access check' : 'Return to Dashboard'
    }
  }

  private get primaryKind(): PrimaryKind {
    if (this.errorCode === '402') {
      if (!this.capabilities.mode()) return 'none'
      if (this.legacyRecovery && !this.accessUnavailable) return this.subscriptionNeedsReactivation
        ? (this.isAdmin ? 'reactivate' : 'none') : 'billingPortal'
      return 'retry'
    }
    if (this.errorCode === '403' || this.errorCode === '404') return 'navigate'
    return 'navigate'
  }



  constructor(
    public userService: UserService,
    public route: ActivatedRoute,
    private router: Router,
    private authService: AuthenticationService,
    private subscriptionService: SubscriptionService,
    public capabilities: RuntimeCapabilitiesService,
  ) { }

  ngOnInit(): void {
    if (this.errorCode === '402') {
      this.refreshAndMaybeRedirect()
    }
  }

  // Staff may have restored access while this tab was in the background.
  @HostListener('window:focus')
  onWindowFocus(): void {
    if (this.errorCode === '402') {
      this.refreshAndMaybeRedirect()
    }
  }

  private refreshAndMaybeRedirect(): void {
    if (this.isOpening) return
    this.isOpening = true
    this.accessMissing = false
    this.accessUnavailable = true
    this.subscriptionService.getSubscriptionDetails().pipe(finalize(() => this.isOpening = false)).subscribe({
      next: details => {
        this.accessUnavailable = !details
        if (details?.status === 'active' || details?.status === 'trialing') {
          this.router.navigate(['projects'])
        }
      },
      error: error => {
        this.accessMissing = error?.error?.code === 'ACCOUNT_ACCESS_MISSING'
        this.accessUnavailable = !this.accessMissing
      },
    })
  }

  primaryAction() {
    switch (this.primaryKind) {
      case 'billingPortal':
        if (this.isOpening) return
        this.isOpening = true
        this.subscriptionService.openSubscriptionBillingPortal(this.portalReturnUrl).pipe(finalize(() => this.isOpening = false)).subscribe({ error: () => {} })
        return
      case 'reactivate':
        if (this.isOpening) return
        this.isOpening = true
        this.subscriptionService.reactivateSubscription().pipe(finalize(() => this.isOpening = false)).subscribe({
          next: () => this.router.navigate(['projects']), error: () => {},
        })
        return
      case 'none': return
      case 'retry':
        this.refreshAndMaybeRedirect()
        return
      case 'navigate':
        this.router.navigate(['projects'])
        return
    }
  }

  secondaryAction() {
    this.authService.logout()
  }
}
